import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../../axios/axiosBaseQuery';
import { Base64 } from '../../../util/Base64';

import exponentialPollingPromise from '../../../util/promise/exponentialPollingPromise';
import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import Util from '../../../util/Util';
import {
  ControlUnitTranslationStaticAPIResponse,
  S3File,
} from './CUTranslation.model';
import axios from 'axios';

const BaseURL = new Util().getTranslationServiceURL();

// Cache duration: 1 day in seconds (24 hours * 60 minutes * 60 seconds)
const ONE_DAY_IN_SECONDS = 24 * 60 * 60;

const CUTranslationsAPI = createApi({
  reducerPath: 'CUTranslationsAPI',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['commander-translation', 'cu-translation', 'translation-file'],
  // Keep cached data for 1 day
  keepUnusedDataFor: ONE_DAY_IN_SECONDS,
  endpoints(build) {
    return {
      // static/commander
      getCommanderTranslationInfo: build.query<
        S3File,
        string | null | undefined
      >({
        query: () => ({
          url: `${BaseURL}/static/commander`,
        }),
        providesTags: ['commander-translation'],
      }),
      // static/{{EncodedDeviceID}}/{{cuId}}
      getCUTranslationInfo: build.query<
        ControlUnitTranslationStaticAPIResponse,
        { deviceId: string; controlUnitId: string | number }
      >({
        // query: (arg) => ({
        //   url: `${BaseURL}/static/${Base64.encode(arg.deviceId)}/${
        //     arg.controlUnitId
        //   }`,
        // }),
        // Do the same but when get 404, return null instead of error
        queryFn: async (
          arg,
          _queryApi,
          _extraOptions,
          fetchWithBQ
        ): Promise<
          { data: ControlUnitTranslationStaticAPIResponse } | { error: unknown }
        > => {
          const result = await fetchWithBQ({
            url: `${BaseURL}/static/${Base64.encode(arg.deviceId)}/${
              arg.controlUnitId
            }`,
          });

          if (result.error && (result.error as any).status === 404) {
            return {
              data: {
                file: null,
                id: 0,
                programNumber: 0,
                programVariant: 0,
                identNumber: null,
                minVersion: 0,
                maxVersion: 0,
                language: 'de',
              },
            };
          }

          return result as
            | { data: ControlUnitTranslationStaticAPIResponse }
            | { error: unknown };
        },

        providesTags: (result, error, arg) => [
          {
            type: 'cu-translation' as const,
            id: `${arg.deviceId}-${arg.controlUnitId}`,
          },
        ],
      }),
      // Download translation file from S3
      downloadTranslationFile: build.query<any, S3File>({
        queryFn: async (s3File) => {
          try {
            // Download file from S3 URL
            const response = await axios.get(s3File.url, {
              withCredentials: false,
            });

            return { data: response.data };
          } catch (error: any) {
            return {
              error: {
                status: error.response?.status || 500,
                data: error.response?.data || error.message,
              },
            };
          }
        },
        // Cache based only on the 'key' field, not the URL
        // URLs can change (new signatures) even with the same key
        serializeQueryArgs: ({ queryArgs }) => {
          return queryArgs.key;
        },
        providesTags: (result, error, arg) => [
          { type: 'translation-file' as const, id: arg.key },
        ],
      }),
    };
  },
});

export const {
  useGetCommanderTranslationInfoQuery,
  useGetCUTranslationInfoQuery,
  useDownloadTranslationFileQuery,
  useLazyDownloadTranslationFileQuery,
} = CUTranslationsAPI;

export default CUTranslationsAPI;
